<template>
  <div class="pt-safe bg-n-900">
    <div class="px-4 py-3 flex text-n-0 items-center h-14">
      <div v-if="currentEmployee" class="flex-1 flex items-center shrink min-w-0">
        <slot name="left">
          <div v-if="showBack" class="ml-6 text-v-400" @click="$emit('back')">
            <div class="p-3 -m-3">
              <l-icon name="bracket-left" size="medium" />
            </div>
          </div>
          <router-link v-else :to="{ name: 'pos' }" class="shrink-0">
            <img src="/icon.png" class="h-8" />
          </router-link>
          <router-link :to="{ name: 'employees' }" class="ml-4 flex items-center min-w-0">
            <l-icon name="user" class="mr-1" size="small" />
            <div class="text-ellipsis min-w-0 text-nowrap overflow-hidden">
              {{ currentEmployee.name }}
            </div>
          </router-link>
          <div
            v-if="tills.cash.length > 1 && selectedCashTill"
            class="flex items-center ml-5 min-w-0"
          >
            <l-icon name="register" class="mr-1" size="small" />
            <div class="text-ellipsis min-w-0 text-nowrap overflow-hidden">
              {{ selectedCashTill.name }}
            </div>
          </div>
        </slot>
      </div>
      <div class="flex justify-center">
        <slot name="center" />
      </div>
      <div class="flex-1 flex justify-end text-n-0 items-center">
        <l-button
          v-if="!isConnected"
          icon="no-wifi"
          size="small"
          color="red"
          class="rounded-full mx-2"
        />
        <slot name="right">
          <div v-if="!isMobile" class="font-body text-base font-normal px-2">
            {{ format(time, 'HH:mm') }}
          </div>
          <div v-if="!showBack && !isBookingOnly" class="flex justify-end p-3">
            <router-link :to="{ name: 'search' }" @click="$emit('close')">
              <l-icon name="search" size="medium" />
            </router-link>
          </div>
          <div class="relative cursor-pointer mx-2" @click="showNotifications = true">
            <div v-if="notificationsAmount > 0" class="absolute z-10 right-0 top-[-0.2rem]">
              <l-badge
                class="cursor-pointer"
                mode="alert"
                type="small"
                :value="notificationsAmount"
              />
            </div>
            <l-lottie
              ref="bellAnim"
              width="1.8rem"
              height="1.8rem"
              :animation-data="bellAnimation"
              :loop="true"
              :auto-play="false"
            />
          </div>
          <div class="p-3 cursor-pointer" @click="showMenu = true">
            <l-icon name="hamburger-3-lines" class="w-6 h-6" size="medium" />
          </div>
        </slot>
      </div>
    </div>

    <side-menu :open="showMenu" @close="showMenu = false" />
    <notifications :open="showNotifications" @close="showNotifications = false" />
  </div>
</template>

<script setup lang="ts">
import { useNow } from '@vueuse/core'
import { format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { ref, useTemplateRef, watch } from 'vue'

import { LBadge, LButton, LIcon, LLottie } from '@last/core-ui/paprika'

import bellAnimation from '@/assets/notification-V4.json'
import SideMenu from '@/components/SideMenu.vue'
import { useNotifications } from '@/composables/useNotifications'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import { useStatusStore } from '@/store/status'
import { useTillStore } from '@/store/till'
import { isMobile } from '@/utils/device-type'

import Notifications from './Notifications.vue'

type Props = {
  showBack?: boolean
}

withDefaults(defineProps<Props>(), {
  showBack: false
})

defineEmits<{
  back: []
  close: []
}>()

const time = useNow({
  interval: 1_000
})

const bellAnim = useTemplateRef('bellAnim')

const { currentEmployee, isBookingOnly } = storeToRefs(useAuthStore())
const { selectedCashTill } = storeToRefs(useTillStore())
const { tills } = storeToRefs(useConfigStore())
const { isConnected } = storeToRefs(useStatusStore())

const { amount: notificationsAmount } = useNotifications()

const showMenu = ref(false)
const showNotifications = ref(false)

watch(notificationsAmount, val => {
  if (bellAnim.value && val > 0) {
    bellAnim.value.play()
  } else if (bellAnim.value) {
    bellAnim.value.stop()
  }
})
</script>
